import { useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";
import { useFavorites } from "@/hooks/useFavorites";
import { sortOptions } from "@/mocks/filters";

interface HotListProps {
  items: Anime[];
}

export default function HotList({ items }: HotListProps) {
  const [sort, setSort] = useState<"popular" | "rating">("popular");
  const { isFavorite, toggleFavorite } = useFavorites();

  const sorted = [...items]
    .sort((a, b) => (sort === "popular" ? b.popularity - a.popularity : b.rating - a.rating))
    .slice(0, 5);

  return (
    <section className="rounded-lg border border-background-200 bg-background-100" aria-labelledby="hot-list-title">
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        <h2 id="hot-list-title" className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
          <i className="ri-fire-fill text-accent-500" />
          热门动漫
        </h2>
        <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
          {["popular", "rating"].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setSort(key as "popular" | "rating")}
              className={`px-2.5 h-6 rounded-full text-[11px] transition-colors cursor-pointer whitespace-nowrap ${
                sort === key ? "bg-background-50 text-foreground-900" : "text-foreground-600"
              }`}
            >
              {sortOptions.find((item) => item.value === key)?.label}
            </button>
          ))}
        </div>
      </div>

      <ul className="mt-3 px-3 pb-3">
        {sorted.map((anime, index) => {
          const favorited = isFavorite(anime.id);
          return (
            <li key={anime.id} className="flex items-center gap-2.5 rounded-md px-1.5 py-2 hover:bg-background-50 transition-colors">
              <span
                className={`w-5 h-5 shrink-0 rounded flex items-center justify-center font-heading text-[11px] font-bold ${
                  index < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-600"
                }`}
              >
                {index + 1}
              </span>
              <Link to={`/detail/${anime.id}`} className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer group">
                <span className="w-9 h-12 shrink-0 rounded overflow-hidden bg-background-200 block">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} 封面`}
                    title={`${anime.title} - 高清动漫在线观看`}
                    className="w-full h-full object-cover"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {anime.title}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-[11px] text-foreground-500">
                    <i className="ri-star-fill text-accent-500 text-[10px]" />
                    {anime.rating.toFixed(1)}
                    <span className="text-background-400">·</span>
                    <span className="truncate">{anime.categories[0]}</span>
                  </span>
                </span>
              </Link>
              <button
                type="button"
                onClick={() => toggleFavorite(anime.id)}
                aria-label={favorited ? `取消收藏 ${anime.title}` : `收藏 ${anime.title}`}
                className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                  favorited ? "bg-accent-100 text-accent-700" : "text-foreground-400 hover:bg-background-200"
                }`}
              >
                <i className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-[13px]`} />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="px-4 pb-4">
        <Link
          to="/list?sort=popular"
          className="h-8 w-full rounded-md border border-background-300 text-[12px] text-foreground-700 flex items-center justify-center gap-1 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          查看完整榜单
          <i className="ri-arrow-right-s-line" />
        </Link>
      </div>
    </section>
  );
}